// Comprehensive diagnostic utilities for debugging chat and map issues

export class DiagnosticUtils {
  private static readonly LOG_PREFIX = '🔍 Diagnostics:';

  // Comprehensive content analysis for debugging markdown issues
  static analyzeContentProcessing(content: string, stage: string): void {
    console.log(`${this.LOG_PREFIX} [${stage}] Content Analysis:`);
    console.log(`${this.LOG_PREFIX} [${stage}] Length: ${content.length}`);
    console.log(`${this.LOG_PREFIX} [${stage}] Line breaks (\\n): ${(content.match(/\n/g) || []).length}`);
    console.log(`${this.LOG_PREFIX} [${stage}] Escaped line breaks (\\\\n): ${(content.match(/\\n/g) || []).length}`);
    console.log(`${this.LOG_PREFIX} [${stage}] Headers (##): ${(content.match(/##/g) || []).length}`);
    console.log(`${this.LOG_PREFIX} [${stage}] Markdown links: ${(content.match(/\[.*?\]\(.*?\)/g) || []).length}`);
    console.log(`${this.LOG_PREFIX} [${stage}] Bold text (**): ${(content.match(/\*\*.*?\*\*/g) || []).length}`);
    console.log(`${this.LOG_PREFIX} [${stage}] Preview (first 300 chars):`);
    console.log(content.substring(0, 300));
    
    if (content.length > 300) {
      console.log(`${this.LOG_PREFIX} [${stage}] Preview (last 300 chars):`);
      console.log(content.substring(content.length - 300));
    }
  }

  // Map URL diagnostic analysis
  static analyzeMapUrl(url: string): void {
    console.log(`${this.LOG_PREFIX} Map URL Analysis:`);
    console.log(`${this.LOG_PREFIX} Full URL: ${url}`);
    console.log(`${this.LOG_PREFIX} Is Google Maps: ${url.includes('google.com/maps') || url.includes('maps.google.com')}`);
    console.log(`${this.LOG_PREFIX} Has coordinates (@): ${url.includes('@')}`);
    console.log(`${this.LOG_PREFIX} Has cbll parameter: ${url.includes('cbll=')}`);
    console.log(`${this.LOG_PREFIX} Has layer=c (street view): ${url.includes('layer=c')}`);
    console.log(`${this.LOG_PREFIX} Has data parameter: ${url.includes('data=')}`);
    
    // Extract potential coordinate patterns
    const patterns = [
      { name: '@lat,lng', regex: /@(-?\d+\.?\d*),(-?\d+\.?\d*)/ },
      { name: 'cbll=lat,lng', regex: /cbll=(-?\d+\.?\d*),(-?\d+\.?\d*)/ },
      { name: 'll=lat,lng', regex: /ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/ },
      { name: 'q=lat,lng', regex: /q=(-?\d+\.?\d*),(-?\d+\.?\d*)/ }
    ];

    patterns.forEach(pattern => {
      const match = url.match(pattern.regex);
      if (match) {
        console.log(`${this.LOG_PREFIX} Found ${pattern.name}: lat=${match[1]}, lng=${match[2]}`);
      }
    });
  }

  // Performance monitoring for API calls
  static timeApiCall<T>(
    apiName: string, 
    apiCall: () => Promise<T>
  ): Promise<T> {
    const startTime = performance.now();
    console.log(`${this.LOG_PREFIX} Starting API call: ${apiName}`);
    
    return apiCall()
      .then(result => {
        const duration = performance.now() - startTime;
        console.log(`${this.LOG_PREFIX} API call ${apiName} completed in ${duration.toFixed(2)}ms`);
        return result;
      })
      .catch(error => {
        const duration = performance.now() - startTime;
        console.error(`${this.LOG_PREFIX} API call ${apiName} failed after ${duration.toFixed(2)}ms:`, error);
        throw error;
      });
  }

  // System health check
  static performHealthCheck(): void {
    console.log(`${this.LOG_PREFIX} System Health Check:`);
    console.log(`${this.LOG_PREFIX} User Agent: ${navigator.userAgent}`);
    console.log(`${this.LOG_PREFIX} Online: ${navigator.onLine}`);
    console.log(`${this.LOG_PREFIX} Local Storage Available: ${this.isLocalStorageAvailable()}`);
    console.log(`${this.LOG_PREFIX} Current URL: ${window.location.href}`);
    console.log(`${this.LOG_PREFIX} Viewport: ${window.innerWidth}x${window.innerHeight}`);
    console.log(`${this.LOG_PREFIX} Memory (if available):`, (performance as any).memory || 'Not available');
  }

  private static isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  // Enhanced test feedback analysis detection
  static testFeedbackDetection(content: string): boolean {
    const originalMarkers = [
      '## 📊', '## ✅', '## ❌', '## 🔄', '## 💡',
      'Impressions Overview', 'Accurate Impressions', 'Analysis of User Notes'
    ];
    
    const actualMarkers = [
      'Target Location:', 'User\'s Impressions and Analysis:',
      '✅ Matches:', '❌ Mismatches:', 'Analysis:', 'Overall,'
    ];
    
    const originalDetected = originalMarkers.some(marker => content.includes(marker));
    const actualDetected = actualMarkers.some(marker => content.includes(marker));
    
    console.log(`${this.LOG_PREFIX} Feedback Detection Test Results:`);
    console.log(`${this.LOG_PREFIX} Original pattern detection: ${originalDetected}`);
    console.log(`${this.LOG_PREFIX} Actual pattern detection: ${actualDetected}`);
    console.log(`${this.LOG_PREFIX} Content line count: ${content.split('\n').length}`);
    console.log(`${this.LOG_PREFIX} Content has line breaks: ${content.includes('\n')}`);
    
    originalMarkers.forEach(marker => {
      if (content.includes(marker)) {
        console.log(`${this.LOG_PREFIX} Found original marker: "${marker}"`);
      }
    });
    
    actualMarkers.forEach(marker => {
      if (content.includes(marker)) {
        console.log(`${this.LOG_PREFIX} Found actual marker: "${marker}"`);
      }
    });
    
    return originalDetected || actualDetected;
  }
}
